import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Share2 } from 'lucide-react';
import { shareElementAsImage } from '../utils/exportUtils';

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ChampionsWall = () => {
  const { API } = useContext(AuthContext);
  const [champions, setChampions] = useState([]);
  const [shareChamp, setShareChamp] = useState(null);
  const [sharingId, setSharingId] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/stats/recent-champions?limit=8`)
      .then((res) => { if (active) setChampions(res.data.champions || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [API]);

  const handleShare = async (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    setShareChamp(c);
    setSharingId(c.tournament_id);
    await new Promise((r) => setTimeout(r, 250));
    try {
      const el = document.getElementById('champ-share-card');
      const res = await shareElementAsImage(el, {
        filename: `champion-${(c.champion.full_name || 'tennis').replace(/\s+/g, '-')}.png`,
        title: `🏆 ${c.champion.full_name} - אלוף ${c.tournament_name}`,
      });
      toast.success(res === 'shared' ? 'התמונה שותפה!' : 'התמונה הורדה - שתף אותה בוואטסאפ/אינסטגרם 🎾');
    } catch (err) {
      console.error(err);
      toast.error('שגיאה בשיתוף');
    } finally {
      setShareChamp(null);
      setSharingId(null);
    }
  };

  if (champions.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-16" dir="rtl" data-testid="champions-wall-section">
      <div className="text-center mb-12">
        <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
          <Trophy className="w-9 h-9 text-amber-500" />
          קיר ההישגים
        </h2>
        <p className="text-base md:text-lg text-gray-500">האלופים האחרונים שהרימו את הגביע 🏆</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {champions.map((c) => (
          <Link
            key={c.tournament_id}
            to={`/tournament/${c.tournament_id}`}
            data-testid={`champion-${c.tournament_id}`}
            className="group relative overflow-hidden rounded-2xl border-2 border-amber-200 bg-gradient-to-b from-amber-50 to-white p-5 flex flex-col items-center text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          >
            <div className="absolute top-2 left-2 text-amber-400/70">
              <Trophy className="w-5 h-5" />
            </div>
            <button
              onClick={(e) => handleShare(e, c)}
              data-testid={`share-champion-${c.tournament_id}`}
              title="שתף הישג"
              disabled={sharingId === c.tournament_id}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 border border-amber-200 flex items-center justify-center text-amber-600 hover:bg-amber-500 hover:text-white transition-colors shadow-sm disabled:opacity-50"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <div className="relative">
              <Avatar className="w-20 h-20 ring-4 ring-amber-300 shadow-md">
                {c.champion.avatar && <AvatarImage src={c.champion.avatar} alt={c.champion.full_name} />}
                <AvatarFallback className="bg-amber-500 text-white text-2xl font-black">
                  {c.champion.full_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-br from-amber-400 to-yellow-500 text-white text-xs font-black px-2 py-0.5 rounded-full shadow ring-2 ring-white">
                אלוף
              </div>
            </div>
            <div className="mt-5 font-extrabold text-slate-900 truncate w-full">{c.champion.full_name}</div>
            <div className="text-xs text-amber-700 font-semibold mt-1 truncate w-full">🏆 {c.tournament_name}</div>
            <div className="text-[11px] text-slate-400 mt-1">
              {c.type === 'daily' ? 'יומית' : 'נוקאאוט'}{c.date ? ` · ${fmtDate(c.date)}` : ''}
            </div>
          </Link>
        ))}
      </div>

      {/* Off-screen share poster */}
      {shareChamp && (
        <div
          id="champ-share-card"
          style={{
            position: 'fixed', left: '-99999px', top: 0, width: '600px', height: '750px',
            background: 'linear-gradient(160deg, #0b1220 0%, #11271f 60%, #064e3b 100%)',
            direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: '48px 40px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
            boxSizing: 'border-box', color: '#fff',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '2px', color: '#34d399' }}>TENNIS PRO ISRAEL</div>
            <div style={{ fontSize: '64px', marginTop: '8px' }}>🏆</div>
            <div style={{ fontSize: '34px', fontWeight: 900, color: '#fbbf24', marginTop: '4px' }}>אלוף התחרות</div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '180px', height: '180px', borderRadius: '50%', margin: '0 auto',
              border: '6px solid #fbbf24', overflow: 'hidden', background: '#fbbf24',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {shareChamp.champion.avatar ? (
                <img src={shareChamp.champion.avatar} alt="" crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '80px', fontWeight: 900, color: '#0b1220' }}>
                  {shareChamp.champion.full_name?.charAt(0) || '?'}
                </span>
              )}
            </div>
            <div style={{ fontSize: '40px', fontWeight: 900, marginTop: '24px' }}>{shareChamp.champion.full_name}</div>
            <div style={{ fontSize: '22px', color: '#fcd34d', fontWeight: 700, marginTop: '12px' }}>
              🏆 {shareChamp.tournament_name}
            </div>
            <div style={{ fontSize: '17px', color: '#94a3b8', marginTop: '10px' }}>
              {(shareChamp.type === 'daily' ? 'תחרות יומית' : 'נוקאאוט')}
              {shareChamp.category ? ` · ${shareChamp.category}` : ''}
              {shareChamp.date ? ` · ${fmtDate(shareChamp.date)}` : ''}
            </div>
          </div>

          <div style={{ fontSize: '16px', color: '#64748b' }}>tennisproisrael.co.il</div>
        </div>
      )}
    </div>
  );
};

export default ChampionsWall;
